<template>
  <div class="tw-space-y-4">
    <p class="tw-text-sm tw-text-gray-600">
      {{ $t('Identification details are required') }}
    </p>

    <form data-test="identification-details-form" @submit.prevent="save">
      <div class="form-group row">
        <label for="identification-date-of-birth" class="col-md-4 control-label">
          {{ $t('Date of birth') }}
        </label>
        <div class="col-md-6">
          <input
            id="identification-date-of-birth"
            v-model="form.dateOfBirth"
            type="date"
            class="form-control"
            :max="yesterdayIso"
            required
            data-test="identification-date-of-birth"
          />
        </div>
      </div>

      <div class="form-group row">
        <label for="identification-nationality" class="col-md-4 control-label">
          {{ $t('Nationality') }}
        </label>
        <div class="col-md-6">
          <select
            id="identification-nationality"
            v-model="form.nationality"
            class="form-control"
            required
            data-test="identification-nationality"
          >
            <option value="" disabled>{{ $t('Select your nationality') }}</option>
            <option v-for="country in countries" :key="country.code" :value="country.code">
              {{ country.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="form-group row">
        <label class="col-md-4 control-label">{{ $t('Document type') }}</label>
        <div class="col-md-6 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2">
          <label v-for="option in documentTypeOptions" :key="option.value" class="tw-mr-2">
            <input
              v-model="form.documentType"
              type="radio"
              :value="option.value"
              required
              :data-test="`identification-document-type-${documentTypeTestId(option.value)}`"
            />
            {{ documentTypeLabel(option.value) }}
          </label>
        </div>
      </div>

      <div class="form-group row">
        <label for="identification-document-number" class="col-md-4 control-label">
          {{ $t('Document number') }}
        </label>
        <div class="col-md-6">
          <input
            id="identification-document-number"
            v-model="form.documentNumber"
            type="text"
            class="form-control"
            required
            minlength="3"
            maxlength="64"
            data-test="identification-document-number"
          />
        </div>
      </div>

      <div class="form-group row">
        <label for="identification-document-expires-at" class="col-md-4 control-label">
          {{ $t('Expiration date') }}
        </label>
        <div class="col-md-6">
          <input
            id="identification-document-expires-at"
            v-model="form.documentExpiresAt"
            type="date"
            class="form-control"
            :min="tomorrowIso"
            required
            data-test="identification-document-expires-at"
          />
        </div>
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger"
        role="alert"
        data-test="identification-error"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="savedRecently"
        class="alert alert-success"
        role="alert"
        data-test="identification-saved"
      >
        {{ $t('Thanks, your identification details have been saved.') }}
      </div>

      <div class="form-group">
        <div class="col-md-6 col-md-offset-4">
          <button
            type="submit"
            class="btn btn-primary no-spinner"
            :disabled="saving"
            data-test="identification-submit"
          >
            {{ saving ? $t('Saving...') : $t('Save') }}
          </button>
        </div>
      </div>
    </form>

    <RightToWorkDocumentsFields
      :worker-global-id="workerGlobalId"
      :selected-nationality="form.nationality"
      bootstrap-form
    />
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { useTranslation } from '@/composables/translation'
import RightToWorkDocumentsFields from '@/components/Identification/RightToWorkDocumentsFields.vue'
import {
  identificationDocumentTypeLabel,
  identificationDocumentTypeOptions,
} from '@/utils/identificationDocumentType'

const { $t } = useTranslation()

const props = defineProps({
  workerGlobalId: { type: String, required: true },
})

const WORKER_IDENTIFICATION_QUERY = gql`
  query workerIdentification($id: ID!) {
    worker(id: $id) {
      __typename
      id
      identification {
        dateOfBirth
        nationality
        documentType
        documentNumber
        documentExpiresAt
      }
    }
  }
`

const UPDATE_WORKER_IDENTIFICATION = gql`
  mutation updateWorkerIdentification($input: UpdateWorkerIdentificationInput!) {
    updateWorkerIdentification(input: $input) {
      dateOfBirth
      nationality
      documentType
      documentNumber
      documentExpiresAt
    }
  }
`

const { result, refetch } = useQuery(
  WORKER_IDENTIFICATION_QUERY,
  () => ({ id: props.workerGlobalId }),
  { fetchPolicy: 'network-only', clientId: 'publicApi' },
)
// The right-to-work requirement follows the saved nationality, so the child query refetches with the mutation.
const { mutate: updateWorkerIdentification } = useMutation(UPDATE_WORKER_IDENTIFICATION, {
  clientId: 'publicApi',
  refetchQueries: ['workerIdentification', 'workerRightToWork'],
  awaitRefetchQueries: true,
})

const form = reactive({
  dateOfBirth: '',
  nationality: '',
  documentType: '',
  documentNumber: '',
  documentExpiresAt: '',
})

const saving = ref(false)
const errorMessage = ref('')
const savedRecently = ref(false)

const tomorrowIso = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().split('T')[0]
})

const yesterdayIso = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().split('T')[0]
})

// Shared with PageManagedWorkerProfile.vue. See utils/identificationDocumentType.
const documentTypeOptions = identificationDocumentTypeOptions()

const documentTypeLabel = identificationDocumentTypeLabel

const documentTypeTestId = (value) => value.toLowerCase().replace(/_/g, '-')

const countries = computed(() => {
  const provided = typeof countriesGlobalVar !== 'undefined' ? countriesGlobalVar : null
  if (!provided || typeof provided !== 'object') {
    return []
  }
  return Object.entries(provided).map(([code, name]) => ({ code, name: $t(name) }))
})

watch(
  () => result.value?.worker?.identification,
  (info) => {
    if (!info) return
    form.dateOfBirth = info.dateOfBirth ?? ''
    form.nationality = info.nationality ?? ''
    form.documentType = info.documentType ?? ''
    form.documentNumber = info.documentNumber ?? ''
    form.documentExpiresAt = info.documentExpiresAt ?? ''
  },
  { immediate: true },
)

const save = async () => {
  saving.value = true
  errorMessage.value = ''
  savedRecently.value = false

  try {
    await updateWorkerIdentification({
      input: {
        dateOfBirth: form.dateOfBirth,
        nationality: form.nationality,
        documentType: form.documentType,
        documentNumber: form.documentNumber,
        documentExpiresAt: form.documentExpiresAt,
      },
    })
    savedRecently.value = true
  } catch (error) {
    const graphQLErrors = error?.graphQLErrors ?? []
    const validation = graphQLErrors[0]?.extensions?.validation
    if (validation && typeof validation === 'object') {
      errorMessage.value = Object.values(validation).flat().join(' ')
    } else {
      errorMessage.value =
        graphQLErrors[0]?.message ||
        $t("We couldn't save your identification details. Please try again.")
    }
  } finally {
    saving.value = false
  }
}
</script>
